<script setup lang="ts">
  import {
    type FlowPage,
    FormFieldContainer,
    FormInput,
    InputField,
    SwitchButton,
    require,
    UiButton,
    isUuid,
    useXPathAction,
    isRecord,
    TabContainer,
    type Tab,
    setNestedValue,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import HiddenFieldsManager from '@/components/form-wizard/HiddenFieldsManager.vue'
  import LogicalOperatorSelect from '@/components/form-wizard/LogicalOperatorSelect.vue'
  import Predicates from '@/components/form-wizard/Predicates.vue'
  import { computed, ref, watch } from 'vue'
  import { useFlowConfigStore } from '@/store/flow-config.store'
  import { useFlowStore } from '@/store/flow.store'
  import axios from 'axios'
  import { axiosClient } from '@/utils/axios-client'
  import { debounce } from '@/utils/debounce'

  const model = defineModel<FlowPage>({ required: true })
  const flowConfigStore = useFlowConfigStore()
  const flowStore = useFlowStore()
  const toastStore = useToastStore()
  const testing = ref(false)
  const { getValue, resolvePath } = useXPathAction()

  const probe = axios.create({
    baseURL: axiosClient.defaults.baseURL ?? '',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
    },
  })

  const tabs: Tab[] = [
    { key: 'general', label: 'general' },
    { key: 'conditions', label: 'conditions' },
    { key: 'hidden', label: 'hidden' },
  ]
  const activeTab = ref<string>('general')

  const hiddenFields = ref<Record<string, string>>(
    Object.fromEntries(model.value.hiddenFields.map(field => [field.key, field.value]))
  )

  const idByKey = computed(
    () =>
      new Map(
        model.value.hiddenFields
          .filter(field => field.id)
          .map(field => [field.key, field.id as string])
      )
  )

  let snapshot: Record<string, string> = { ...hiddenFields.value }
  let timer: ReturnType<typeof setTimeout> | undefined

  watch(
    hiddenFields,
    next => {
      clearTimeout(timer)
      const payload = { ...next }
      timer = setTimeout(() => void persist(payload), 500)
    },
    { deep: true }
  )

  watch(
    () => model.value.predicates,
    () => updateFlowPage(),
    { deep: true }
  )

  async function persist(next: Record<string, string>) {
    const pageId = model.value.id
    const previousKeys = new Set(Object.keys(snapshot))
    const nextKeys = new Set(Object.keys(next))

    const created = [...nextKeys].filter(key => !previousKeys.has(key))
    const removed = [...previousKeys].filter(key => !nextKeys.has(key))
    const updated = [...nextKeys].filter(
      key => previousKeys.has(key) && snapshot[key] !== next[key]
    )

    try {
      await Promise.all([
        ...created.map(key =>
          flowConfigStore.createHiddenField({ key, value: next[key] ?? '', pageId }, pageId)
        ),
        ...removed.map(key => {
          const id = idByKey.value.get(key)
          return id ? flowConfigStore.deleteHiddenField(id) : Promise.resolve()
        }),
        ...updated.map(key => {
          const id = require(idByKey.value.get(key))
          if (!isUuid(id)) {
            throw new Error('key is not a uuid')
          }
          return flowConfigStore.updateHiddenField(
            { id, key, value: next[key] ?? '', pageId },
            pageId
          )
        }),
      ])
      snapshot = { ...next }
      toastStore.success('Configuratie succesvol bijgewerkt')
    } catch {
      toastStore.error(
        'Er is een fout opgetreden bij het bijwerken van de configuratie. Probeer het opnieuw.'
      )
    }
  }

  async function submitUpdate() {
    try {
      await flowConfigStore.updateFlowPage(model.value.id)
      toastStore.success('Configuratie succesvol bijgewerkt')
    } catch (error) {
      toastStore.error(
        'Er is een fout opgetreden bij het bijwerken van de configuratie. Probeer het opnieuw.'
      )
      throw error
    }
  }

  const updateFlowPage = debounce(async () => {
    await submitUpdate()
  }, 500)

  async function test() {
    try {
      testing.value = true

      const endpoint = model.value.endpoint

      for (const field of model.value.hiddenFields) {
        setNestedValue(flowStore.flowData, field.key, getValue(field.value))
      }

      const payload = resolvePath(model.value.dto)

      if (!isRecord(payload)) {
        if (model.value.dto.length > 0) {
          return toastStore.error('Kon dto target niet vinden')
        }
      }

      const headers = Object.fromEntries(
        Object.entries(model.value.headers ?? {}).map(([key, value]) => [
          key,
          getValue(value) ?? value,
        ])
      )

      await probe.request({
        url: require(getValue(endpoint)),
        method: 'POST',
        data: payload,
        headers,
      })

      toastStore.success('Fetch geslaagd, resultaat opgeslagen in de context')
    } catch (error) {
      console.error(error)
      toastStore.error('Fetch mislukt')
    } finally {
      testing.value = false
    }
  }
</script>

<template>
  <TabContainer v-model="activeTab" :tabs="tabs">
    <template #general>
      <FormFieldContainer label="Name">
        <template #action-button>
          <SwitchButton size="sm" v-model="model.showName" @update:modelValue="submitUpdate()" />
        </template>
        <FormInput
          placeholder="Voeg een paginanaam toe..."
          required
          v-model="model.name"
          @input="updateFlowPage()"
        />
      </FormFieldContainer>
      <FormInput
        label="Endpoint"
        placeholder="Voeg een endpoint toe..."
        required
        v-model="model.endpoint"
        @input="updateFlowPage()"
      />
      <FormFieldContainer label="Show Submit">
        <SwitchButton size="sm" v-model="model.showSubmit" @update:modelValue="submitUpdate()" />
      </FormFieldContainer>
      <FormFieldContainer label="Headers">
        <HiddenFieldsManager v-model="model.headers" @update:modelValue="updateFlowPage()" />
      </FormFieldContainer>
      <FormFieldContainer label="Online Only">
        <SwitchButton size="sm" v-model="model.onlineOnly" @update:modelValue="submitUpdate()" />
      </FormFieldContainer>
      <FormInput
        label="DTO Target Path"
        placeholder="Voer een DTO in..."
        required
        v-model="model.dto"
        @input="updateFlowPage()"
      />

      <FormFieldContainer label="Success Message">
        <InputField
          type="text"
          :textarea="true"
          v-model="model.successMessage"
          @update:modelValue="updateFlowPage()"
        />
      </FormFieldContainer>

      <div class="test-row">
        <UiButton label="Test" :disabled="testing" @click="test" />
      </div>
    </template>

    <template #conditions>
      <Predicates v-model="model.predicates" />

      <LogicalOperatorSelect
        v-model="model.predicateCombinator"
        label="Filter Logic"
        nullable
        @update:modelValue="submitUpdate()"
      />
    </template>

    <template #hidden>
      <HiddenFieldsManager v-model="hiddenFields" />
    </template>
  </TabContainer>
</template>

<style scoped>
  .test-row {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  }
</style>
